import React, { useState, useEffect, useRef, Fragment } from "react";
import { useAuth } from "./AuthContext";
import { Button, Form, Alert, Modal } from "react-bootstrap";
import RetriggerModal from "./RetriggerModal";
import RejectModal from "./RejectModal";
import { API_BASE_URL } from "../../config";
// proposed_loan_details and member_details money columns store PAISE.
import { toPaise, toRupees } from "../../utils/money";
// The agency/amount pairing, defined once. It is deliberately out of step —
// agency 1 pairs with amount 2 — so never pair these columns by name.
import { FUNDING_ROWS } from "../../utils/validationSchema";
import { CheckIcon, CloseIcon, RefreshIcon } from "../ui/Icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const ActionButtons = ({ updateList, item }) => {
  const navigate = useNavigate();
  const { roleName, data } = useAuth();
  const [showDisbursementModal, setShowDisbursementModal] = useState(false);
  /**
   * Where the money for this loan would come from, in priority order:
   * OWN FUND, then the funding agencies, then EMI money collected but not yet
   * applied to instalments.
   *
   * Read-only. It never blocks the disbursement — during busy hours the operator
   * knows whether a drawdown has arrived and the software does not — but the
   * operator should not learn afterwards that a loan was funded from members'
   * collected EMI money.
   */
  const [fundingPlan, setFundingPlan] = useState(null);
  const [memberId, setMemberId] = useState("");
  const [comment, setComment] = useState("");
  const [retriggerToRole, setRetriggerToRole] = useState("");
  const [showRetriggerModal, setShowRetriggerModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [fundingAgencies, setFundingAgencies] = useState([]);
  const [selectedFundingAgency1, setSelectedFundingAgency1] = useState("");
  const [selectedFundingAgency2, setSelectedFundingAgency2] = useState("");
  const [selectedFundingAgency3, setSelectedFundingAgency3] = useState("");
  // How the money physically reaches the member. Until this existed every
  // disbursement was written with cash_type NULL, which ledgerPosting.js reads
  // as BANK (`isCash = cash_type === 'cash'`), so a cash payout was posted
  // against a bank account the money never left.
  //
  // It also decides whether back-dated instalments are netted off the proceeds:
  // bank money is already in the FPO's account; cash has been handed over.
  const [disbursementMode, setDisbursementMode] = useState("BANK");
  const [backdatedNote, setBackdatedNote] = useState("");
  const [fundingAmount1, setFundingAmount1] = useState(0);
  const [fundingAmount2, setFundingAmount2] = useState(0);
  const [fundingAmount3, setFundingAmount3] = useState(0);
  const [error, setError] = useState("");
  const [editableInsuranceAmount, setEditableInsuranceAmount] = useState("");
  const [editableSecurityDeposit, setEditableSecurityDeposit] = useState("");
  // [DUPLICATE-DISBURSEMENT GUARD] re-entrancy lock so the disbursement sequence cannot be
  // double-submitted (a double-click posted the disbursement + fee/GST twice for VAHITHA BEGAM).
  const [isDisbursing, setIsDisbursing] = useState(false);
  const insuranceInitialized = useRef(false);

  const loanDetails =
    item?.fk_member_details_hasOne_proposed_loan_details_memberId || {};
  // proposed_loan_details money columns store PAISE (Stage 2). Everything below
  // — the 2% processing charge, GST, security deposit, insurance, amountToRelease
  // and the /api/funding-plan query — is expressed in RUPEES, and
  // transactions.amount is still a rupee column until Stage 3. So convert once
  // here and let the rest of the component stay exactly as it was.
  const loanAmount = toRupees(loanDetails.loanAmount);
  const rateOfInterest = loanDetails.rateOfInterest || 0;
  const tenureInMonths = loanDetails.tenureInMonths || 0;
  const initialFundingAmount1 = toRupees(loanDetails.fundingAmount1);
  const initialFundingAmount2 = toRupees(loanDetails.fundingAmount2);
  const initialFundingAmount3 = toRupees(loanDetails.fundingAmount3);
  const initialFundingAgencyId = loanDetails.fundingAgencyId || 0;
  const initialFundingAgencyId2 = loanDetails.fundingAgencyId2 || 0;
  const initialFundingAgencyId3 = loanDetails.fundingAgencyId3 || 0;

  const processingCharge = Math.round((loanAmount * 2) / 100);
  const gst = Math.round((processingCharge * 18) / 100);
  const monthlyRate = rateOfInterest / 12 / 100;
  const defaultSecurityDeposit = Math.round((loanAmount * 1) / 100);
  const defaultInsuranceAmount = Math.round((loanAmount * 1) / 100);

  const securityDeposit = editableSecurityDeposit;
  const insuranceAmount = editableInsuranceAmount;

  const amountToRelease =
    loanAmount - processingCharge - gst - parseFloat(editableSecurityDeposit || 0) - parseFloat(editableInsuranceAmount || 0);

  // The agency a slot has selected.
  //
  // Number() on BOTH sides is required, not defensive. funding_agencies.id is a
  // number, but a <select> hands back a STRING — so `agency.id === selected`
  // was 1 === "1", i.e. false, and the lookup returned undefined the moment the
  // operator touched the dropdown. That is what printed "(N/A)" beside an
  // agency that WAS selected. It worked on first render only because the
  // initial value arrives from loanDetails as a number, which made it look
  // intermittent.
  const agencyNameFor = (selectedId) => {
    if (selectedId === "" || selectedId === null || selectedId === undefined) {
      return "";
    }
    const match = fundingAgencies.find(
      (agency) => Number(agency.id) === Number(selectedId)
    );
    return match ? match.agencyName : "";
  };

  // One row per funding slot, in the order the modal draws them.
  //
  // The agency/amount pairing comes from FUNDING_ROWS in validationSchema.js so
  // it is defined ONCE. It is deliberately out of step — agency 1 pairs with
  // amount 2 — and ledgerPosting.js:2582 reads it the same way. The state
  // setters below preserve that pairing exactly; the payload still writes each
  // column by name, so what reaches the database is unchanged.
  const fundingRows = [
    {
      agency: FUNDING_ROWS[0].agency,
      selectedAgency: selectedFundingAgency1,
      setSelectedAgency: setSelectedFundingAgency1,
      amountValue: fundingAmount2,
      setAmountValue: setFundingAmount2,
    },
    {
      agency: FUNDING_ROWS[1].agency,
      selectedAgency: selectedFundingAgency2,
      setSelectedAgency: setSelectedFundingAgency2,
      amountValue: fundingAmount1,
      setAmountValue: setFundingAmount1,
    },
    {
      agency: FUNDING_ROWS[2].agency,
      selectedAgency: selectedFundingAgency3,
      setSelectedAgency: setSelectedFundingAgency3,
      amountValue: fundingAmount3,
      setAmountValue: setFundingAmount3,
    },
  ];

  const fundedTotal = fundingRows.reduce(
    (sum, row) => sum + (Number(row.amountValue) || 0),
    0
  );
  // A rupee of tolerance, matching the validation schema: the columns are
  // integers, so a real mismatch is always at least 1.
  const fundingReconciles = Math.abs(fundedTotal - loanAmount) < 1;

  const inr = (n) =>
    `Rs.${Number(n || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  useEffect(() => {
    const fetchFundingAgencies = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/getModelData/funding_agencies`
        );
        const all = response.data.data || [];

        // Only OWN FUND is offered for the FPO's own money. OWN FUNDING 1/2/4
        // and OWN FUND 3 are the same thing under four older names — all
        // funding_type 'equity' — and offering five ways to say it is how the
        // lending ended up spread across five accounts.
        const ownFund = all.find(
          (a) => String(a.agencyName || "").trim() === "OWN FUND"
        );
        const isRetiredVariant = (id) => {
          const a = all.find((x) => Number(x.id) === Number(id));
          return (
            a &&
            a.funding_type === "equity" &&
            String(a.agencyName || "").trim() !== "OWN FUND"
          );
        };
        // A stored variant is no longer in the list, so the <select> would render
        // blank and an operator re-saving the loan would silently clear its
        // funding. Map it onto OWN FUND for display instead.
        const forDisplay = (id) =>
          isRetiredVariant(id) && ownFund ? ownFund.id : id;

        setFundingAgencies(
          all.filter(
            (a) =>
              a.funding_type !== "equity" ||
              String(a.agencyName || "").trim() === "OWN FUND"
          )
        );
        setSelectedFundingAgency1(forDisplay(initialFundingAgencyId));
        setSelectedFundingAgency2(forDisplay(initialFundingAgencyId2));
        setSelectedFundingAgency3(forDisplay(initialFundingAgencyId3));
        setFundingAmount1(initialFundingAmount1);
        setFundingAmount2(initialFundingAmount2);
        setFundingAmount3(initialFundingAmount3);
      } catch (error) {
        setError("Failed to fetch funding agencies. Please try again.");
      }
    };
    if (roleName === "admin") {
      fetchFundingAgencies();
    }
  }, [initialFundingAgencyId, initialFundingAgencyId2, initialFundingAgencyId3, roleName, initialFundingAmount1, initialFundingAmount2, initialFundingAmount3]);

  // Re-read whenever the modal opens or the releasable amount changes — the
  // deductions are editable, so the figure being funded moves as the operator
  // types.
  useEffect(() => {
    const branchId = data?.branchId;
    if (!showDisbursementModal || !branchId || !(amountToRelease > 0)) {
      setFundingPlan(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/ledger/funding-plan`, {
          // The mode decides WHICH account the money leaves from, so the
          // advisory has to know it. Without it the server pooled cash + bank
          // and told Palani Rs.22,783 was available while the bank held
          // Rs.7,360 — and the transfer posted, taking the bank negative.
          params: { branchId, amount: amountToRelease, mode: disbursementMode },
        });
        if (!cancelled) setFundingPlan(res.data?.data || null);
      } catch {
        // A funding position we cannot read must not block a disbursement.
        // The posting still happens and the ledger still records it; the
        // operator simply loses the advisory.
        if (!cancelled) setFundingPlan(null);
      }
    })();
    return () => { cancelled = true; };
    // disbursementMode belongs here: without it, switching CASH/BANK leaves the
    // warning describing the PREVIOUS mode, which is worse than no warning —
    // the operator reads a figure that no longer applies.
  }, [showDisbursementModal, amountToRelease, data?.branchId, disbursementMode]);

  useEffect(() => {
    if (!insuranceInitialized.current && loanAmount > 0) {
      setEditableInsuranceAmount(Math.round((loanAmount * 1) / 100));
      setEditableSecurityDeposit(Math.round((loanAmount * 1) / 100));
      insuranceInitialized.current = true;
    }
  }, [loanAmount]);

  const handleSubmit = async (id, msg, modalName) => {
    setMemberId(id);
    setError("");
    if (roleName === "admin" && modalName === "submit") {
      setShowDisbursementModal(true);
    } else {
      const message = (
        <Alert variant="info">
          <p>{msg}</p>
        </Alert>
      );
      if (modalName === "submit") {
        handleNonAdminSubmit(id, message);
      } else if (modalName === "retrigger") {
        setShowRetriggerModal(true);
      } else if (modalName === "reject") {
        setShowRejectModal(true);
      }
    }
  };

  const handleNonAdminSubmit = async (id, message) => {
    try {
      setError("");
      const payload = {
        model: "member_details",
        type: "Edit",
        id: id,
      };
      if (roleName === "Data Entry Operator") {
        payload.dataEntryOperatorStatus = "submitted";
        payload.dataEntryOperatorStatusUpdatedAt = new Date().toISOString();
      }
      // The Branch Manager's own approval. Its absence was a hole in the
      // workflow, not a deliberate omission: the BM could Retrigger and Reject
      // but never approve, so branchManagerStatus only ever reached "submitted"
      // through the superadmin repair modal or the CSV importer. Every organic
      // loan stalled between the DEO and the admin, and the stage lock below
      // ("once the BM submits, the loan is no longer editable") could never fire.
      if (roleName === "Branch Manager") {
        payload.branchManagerStatus = "submitted";
        payload.branchManagerStatusUpdatedAt = new Date().toISOString();
      }
      await axios.post(`${API_BASE_URL}/api/addDetails`, payload);
      updateList();
    } catch (error) {
      setError("Failed to submit. Please try again.");
    }
  };

  const handleDisbursementConfirm = async () => {
    // [DUPLICATE-DISBURSEMENT GUARD] block re-entry while a disbursement is already in flight.
    if (isDisbursing) return;
    setIsDisbursing(true);
    try {
      setError("");
      // Update proposed_loan_details
      const fundingPayload = {
        id: loanDetails.id,
        model: "proposed_loan_details",
        type: "Edit",
        fundingAgencyId: selectedFundingAgency1,
        fundingAgencyId2: selectedFundingAgency2,
        fundingAgencyId3: selectedFundingAgency3,
        // The operator edits these in RUPEES; the columns store PAISE.
        fundingAmount1: toPaise(fundingAmount1),
        fundingAmount2: toPaise(fundingAmount2),
        fundingAmount3: toPaise(fundingAmount3),
      };
      await axios.post(`${API_BASE_URL}/api/addDetails`, fundingPayload);

      // Submit disbursement
      const disbursementPayload = {
        model: "member_details",
        type: "Edit",
        id: memberId,
        adminStatus: "submitted",
        adminStatusUpdatedAt: new Date().toISOString(),
        // These four member_details columns store PAISE (Stage 2); the values
        // above are all in rupees. securityDeposit is NOT in that set — it is
        // still a rupee column, so it is sent unscaled.
        processingCharge: toPaise(processingCharge),
        isProcessingChargePaid: false,
        gst: toPaise(gst),
        isGstPaid: false,
        securityDeposit: parseFloat(editableSecurityDeposit) || 0,
        isSecurityDepositPaid: false,
        isLoanInsured: true,
        insuranceAmount: toPaise(editableInsuranceAmount),
        isInsuranceAmountPaid: false,
        amountToRelease: toPaise(amountToRelease),
        // Not a column: the server reads it to check the CHOSEN account holds the
        // money (Cash/Bank may never go negative) and strips it before saving.
        disbursementMode,
      };
      await axios.post(`${API_BASE_URL}/api/addDetails`, disbursementPayload);

      // 1. Credit full Loan Amount to member's savings account
      const creditPayload = {
        model: "transactions",
        type: "Add",
        transactionType: "deposit",
        referenceId: memberId,
        accountType: "01", // Assuming 01 is Savings. Will use same accountType for debits.
        customerUniqueId: item.customerUniqueId,
        amount: loanAmount,
        narration: `Loan disbursement for member ${memberId}`,
        date: new Date().toISOString().split('T')[0],
        debitCredit: "credit",
        // Lowercase 'cash' is what ledgerPosting.js tests for; anything else
        // resolves to the bank account.
        cash_type: disbursementMode === "CASH" ? "cash" : "ELECTRONIC",
        role: roleName,
      };
      await axios.post(`${API_BASE_URL}/api/addDetails`, creditPayload);

      // Helper function to add withdrawal transactions
      const addWithdrawal = async (amount, narration) => {
        if (amount > 0) {
          const payload = {
            model: "transactions",
            type: "Add",
            transactionType: "withdraw",
            referenceId: memberId,
            accountType: "01",
            customerUniqueId: item.customerUniqueId,
            amount: amount,
            narration: narration,
            date: new Date().toISOString().split('T')[0],
            debitCredit: "debit",
            // Matters most on the proceeds withdrawal: postProceedsWithdrawal
            // credits Cash or Bank from this, so a cash payout must not be
            // recorded as leaving the bank.
            cash_type: disbursementMode === "CASH" ? "cash" : "ELECTRONIC",
            role: roleName,
          };
          await axios.post(`${API_BASE_URL}/api/addDetails`, payload);
        }
      };

      // 2. Perform separate debits for each deduction
      await addWithdrawal(processingCharge, `Processing Fee deduction for loan ${memberId}`);
      await addWithdrawal(gst, `GST deduction on processing fee for loan ${memberId}`);
      await addWithdrawal(parseFloat(editableInsuranceAmount) || 0, `Insurance deduction for loan ${memberId}`);
      await addWithdrawal(parseFloat(editableSecurityDeposit) || 0, `Security Deposit deduction for loan ${memberId}`);

      // 2b. Collect instalments that were already overdue when the loan was
      // approved — HERE, and nowhere else.
      //
      // The window used to be one step wide, because the proceeds withdrawal
      // that followed handed the loan straight back and left nothing to collect
      // from. Proceeds are no longer withdrawn here, so the member's balance
      // stays up and that pressure is gone — but this remains the right place:
      // it is the moment the loan exists and the money is in the account.
      //
      // Deliberately NOT fatal: the money has already moved. A failure here
      // leaves the rows Pending for the monthly sweep or the Mark Received
      // button, which is recoverable; unwinding a committed disbursement is not.
      //
      // Declared OUTSIDE the try so the catch path leaves it 0, and so it is
      // still in scope below where it is shown to the operator: what they must
      // pay out is amountToRelease MINUS this.
      let backdatedCollected = 0;
      try {
        const backdated = await axios.post(
          `${API_BASE_URL}/api/v2/collect-backdated`,
          { memberId, mode: disbursementMode }
        );
        const d = backdated.data || {};
        // collectedAmount is the RUPEES taken. `collected` beside it is a COUNT
        // of instalments ("8 back-dated instalment(s) collected") and must not
        // be used here. CASH mode returns 0 by design.
        backdatedCollected = Number(d.collectedAmount) || 0;
        if (d.collected > 0 || d.leftPending > 0) {
          setBackdatedNote(d.message || "");
        }
      } catch (backdatedError) {
        console.error("Back-dated collection failed:", backdatedError);
        setBackdatedNote(
          "Back-dated instalments could not be collected automatically — " +
          "they remain pending and can be collected from Member Details."
        );
      }

      // 3. The proceeds are NOT handed over here.
      //
      // Disbursement parks the money: postLoanDisbursement credits Loan Proceeds
      // Payable, never Cash or Bank (ledgerPosting.js:1723), so after this modal
      // the FPO is holding money it owes the member. The member is paid when an
      // operator records a withdrawal against them — Member Details > the
      // transaction form — which is when the notes actually move.
      //
      // That manual withdrawal posts the identical entry this step used to:
      // postProceedsWithdrawal (ledgerPosting.js:2117) draws from Loan Proceeds
      // Payable first and only then from Member Savings, deciding the split from
      // undrawnProceedsInPaise. It is generic and does not depend on being called
      // from here, so nothing is lost by leaving it to the operator.
      //
      // WHAT THE OPERATOR MUST PAY is amountToRelease MINUS any back-dated
      // instalments collected in step 2b — the money those took has already been
      // recovered, so paying the gross amount pays it out twice. No screen shows
      // that net figure today; scripts/report_undrawn_proceeds.js and the
      // member's ledger both carry it.
      //
      // Until that withdrawal is recorded, the proceeds sit undrawn and Cash and
      // Bank do not move. That is the point of the change.
      //
      // Tell the operator here, in the note that OUTLIVES the modal (the modal
      // closes on success, so anything inside it is lost). Without this the
      // disbursement looks finished while the member has not been paid.
      const payable = Math.round(
        Math.max(0, parseFloat(amountToRelease) - backdatedCollected) * 100
      ) / 100;
      const money = (n) =>
        `Rs.${Number(n).toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      const payoutNote =
        `The proceeds have NOT been paid out. Record a withdrawal of ` +
        `${money(payable)} from Member Details when the member takes the money` +
        (backdatedCollected > 0
          ? ` (${money(amountToRelease)} less ${money(backdatedCollected)} of ` +
            `back-dated instalments already collected).`
          : ".");
      setBackdatedNote((prev) => (prev ? `${prev} ${payoutNote}` : payoutNote));

      updateList();
      setShowDisbursementModal(false);
    } catch (error) {
      // A funds refusal carries the reason (which account, how much); show it.
      setError(
        error?.response?.data?.error ||
        "Failed to submit disbursement or add transaction. Please try again."
      );
    } finally {
      setComment("");
      setIsDisbursing(false);
    }
  };

  const handleCloseDisbursement = () => {
    setShowDisbursementModal(false);
    setError("");
    setSelectedFundingAgency1(initialFundingAgencyId);
    setSelectedFundingAgency2(initialFundingAgencyId2);
    setSelectedFundingAgency3(initialFundingAgencyId3);
    setFundingAmount1(initialFundingAmount1);
    setFundingAmount2(initialFundingAmount2);
    setFundingAmount3(initialFundingAmount3);
  };

  const handleRetriggerConfirm = async () => {
    try {
      setError("");
      const formattedMessage =
        "\n" +
        comment +
        "\n---By " +
        roleName +
        "\n-- " +
        new Date().toISOString() +
        "\n******** ";
      const payload = {
        id: memberId,
        model: "member_details",
        type: "Edit",
      };
      if (retriggerToRole === "Data Entry Operator") {
        payload.dataEntryOperatorStatus = "retrigger";
        payload.dataEntryOperatorMessage = formattedMessage;
        payload.dataEntryOperatorStatusUpdatedAt = new Date().toISOString();
      }
      if (retriggerToRole === "Branch Manager") {
        payload.branchManagerStatus = "retrigger";
        payload.branchManagerMessage = formattedMessage;
        payload.branchManagerStatusUpdatedAt = new Date().toISOString();
      }
      await axios.post(`${API_BASE_URL}/api/addDetails`, payload);
      updateList();
    } catch (error) {
      setError("Failed to retrigger. Please try again.");
    } finally {
      setShowRetriggerModal(false);
      setComment("");
      setRetriggerToRole("");
    }
  };

  const handleCloseRetrigger = () => {
    setShowRetriggerModal(false);
    setComment("");
    setRetriggerToRole("");
    setError("");
  };

  const handleRejectConfirm = async () => {
    try {
      setError("");
      const formattedMessage =
        "\n" +
        comment +
        "\n---By " +
        roleName +
        "\n-- " +
        new Date().toISOString() +
        "\n******** ";
      const payload = {
        model: "member_details",
        type: "Edit",
        id: memberId,
      };
      if (roleName === "Data Entry Operator") {
        payload.dataEntryOperatorStatus = "rejected";
        payload.dataEntryOperatorMessage = formattedMessage;
        payload.dataEntryOperatorStatusUpdatedAt = new Date().toISOString();
      }
      if (roleName === "Branch Manager") {
        payload.branchManagerStatus = "rejected";
        payload.branchManagerMessage = formattedMessage;
        payload.branchManagerStatusUpdatedAt = new Date().toISOString();
      }
      if (roleName === "admin") {
        payload.adminStatus = "rejected";
        payload.adminMessage = formattedMessage;
        payload.adminStatusUpdatedAt = new Date().toISOString();
      }
      await axios.post(`${API_BASE_URL}/api/addDetails`, payload);
      updateList();
    } catch (error) {
      setError("Failed to reject. Please try again.");
    } finally {
      setShowRejectModal(false);
      setComment("");
    }
  };

  const handleCloseReject = () => {
    setShowRejectModal(false);
    setComment("");
    setError("");
  };

  // A loan the admin has already approved is finished with the workflow, so no
  // stage may act on it again — whatever its own stage column still says.
  //
  // WHY: each role's gate below tests only ITS OWN status column. 5,052 loans
  // carry adminStatus Approved/submitted while dataEntryOperatorStatus is still
  // 'pending' (historical imports that never advanced the DEO stage), so a DEO
  // saw an approve tick on loans approved long ago — 1,167 of them already
  // disbursed. JANANI J showed it on two closed loans while her one live loan
  // correctly showed none. Four loans do the same to a Branch Manager.
  //
  // The backend already refuses these (assertApplicationEditable returns 403),
  // so this only stops staff clicking a button that was always going to fail.
  const adminDecided = ["approved", "submitted"].includes(
    String(item?.adminStatus || "").toLowerCase().trim()
  );
  const loanLeftApplication = ["opened", "foreclosed", "preclosed", "closed"].includes(
    String(item?.loanStatus || "").toLowerCase().trim()
  );
  // The admin's own gate already requires adminStatus pending/retrigger, so
  // adminDecided can never exclude a decision they still have to make.
  const workflowClosed = adminDecided || loanLeftApplication;

  return (
    <Fragment>
      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}
      {/* Outside the modal, which closes on success — otherwise the result of
          the back-dated collection would vanish with it. A skipped instalment
          must be seen, not left in a log: that silence is the whole reason this
          collection was moved. */}
      {backdatedNote && (
        <Alert variant="info" onClose={() => setBackdatedNote("")} dismissible>
          {backdatedNote}
        </Alert>
      )}
      <div className="flex items-center gap-1.5 justify-center">
        {!workflowClosed &&
          ((roleName === "Data Entry Operator" &&
          ["pending", "retrigger"].includes(item.dataEntryOperatorStatus)) ||
          // The BM approves once the DEO has submitted and their own stage is
          // still open. Same shape as the two rows around it; without this the
          // BM had Retrigger and Reject but no way to pass the loan forward.
          (roleName === "Branch Manager" &&
            item.dataEntryOperatorStatus === "submitted" &&
            ["pending", "retrigger"].includes(item.branchManagerStatus)) ||
          (roleName === "admin" &&
            item.dataEntryOperatorStatus === "submitted" &&
            item.branchManagerStatus === "submitted" &&
            ["pending", "retrigger"].includes(item.adminStatus))) && (
          <button
            onClick={() =>
              handleSubmit(item.id, "Are you sure you want to Submit?", "submit")
            }
            title="Submit / Approve"
            className="inline-flex items-center justify-center w-7 h-7 rounded-md border shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary/20 text-emerald-600 bg-emerald-50 border-emerald-200 hover:bg-emerald-100 hover:text-emerald-700"
          >
            <CheckIcon className="w-4 h-4" strokeWidth={3} />
          </button>
        )}
        {!workflowClosed &&
          ((roleName === "Branch Manager" &&
          item.dataEntryOperatorStatus === "submitted" &&
          // "retrigger" included so a loan the admin sent back to the BM can be
          // passed down to the DEO in turn. Every other gate in this file already
          // treats retrigger as equivalent to pending; this one did not, which
          // left an admin-retriggered loan stuck with the BM.
          ["pending", "retrigger"].includes(item.branchManagerStatus)) ||
          (roleName === "admin" &&
            item.dataEntryOperatorStatus === "submitted" &&
            item.branchManagerStatus === "submitted" &&
            item.adminStatus === "pending")) && (
          <button
            onClick={() =>
              handleSubmit(
                item.id,
                "Are you sure you want to Retrigger?",
                "retrigger"
              )
            }
            title="Retrigger"
            className="inline-flex items-center justify-center w-7 h-7 rounded-md border shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary/20 text-amber-600 bg-amber-50 border-amber-200 hover:bg-amber-100 hover:text-amber-700"
          >
            <RefreshIcon className="w-3.5 h-3.5" strokeWidth={3} />
          </button>
        )}
        {!workflowClosed &&
          ((roleName === "Data Entry Operator" &&
          ["pending", "retrigger"].includes(item.dataEntryOperatorStatus)) ||
          (roleName === "Branch Manager" &&
            item.dataEntryOperatorStatus === "submitted" &&
            ["pending", "retrigger"].includes(item.branchManagerStatus)) ||
          (roleName === "admin" &&
            item.dataEntryOperatorStatus === "submitted" &&
            item.branchManagerStatus === "submitted" &&
            ["pending", "retrigger"].includes(item.adminStatus))) && (
          <button
            onClick={() =>
              handleSubmit(item.id, "Are you sure you want to Reject?", "reject")
            }
            title="Reject"
            className="inline-flex items-center justify-center w-7 h-7 rounded-md border shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary/20 text-rose-600 bg-rose-50 border-rose-200 hover:bg-rose-100 hover:text-rose-700"
          >
            <CloseIcon className="w-3.5 h-3.5" strokeWidth={3} />
          </button>
        )}
      </div>
      {showDisbursementModal && (
        <Modal show={showDisbursementModal} onHide={handleCloseDisbursement}>
          <Modal.Header closeButton>
            <Modal.Title>Loan Disbursement</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Alert variant="info">
              <p>
                <strong>Loan Amount:</strong> {loanAmount} <br />
                <strong>Rate of Interest:</strong> {rateOfInterest}% <br />
                <strong>Tenure:</strong> {tenureInMonths} months <br />
                <strong>Processing Charge (2%):</strong> {processingCharge} <br />
                <strong>GST (18% on PC):</strong> {gst} <br />
                <strong>Security Deposit:</strong> {parseFloat(editableSecurityDeposit) || 0} <br />
                <strong>Insurance Amount:</strong> {parseFloat(editableInsuranceAmount) || 0} <br />
                <strong>Amount to Release:</strong> {amountToRelease} <br />
              </p>
            </Alert>

            {/* Where the money comes from. Shown before the operator confirms,
                because "this loan is funded from members' collected EMI money"
                is not something to discover afterwards from a report. */}
            {fundingPlan && (
              <Alert variant={fundingPlan.allowed ? (fundingPlan.usesCollections > 0 ? "warning" : "light") : "danger"}>
                <strong>Funding this loan</strong>
                <table className="w-100 mt-2 mb-0" style={{ fontSize: "0.9rem" }}>
                  <tbody>
                    {fundingPlan.sources.map((s) => (
                      <tr key={s.source}>
                        <td>{s.source}</td>
                        <td className="text-end">
                          ₹{Number(s.amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                        </td>
                        <td className="text-end" style={{ width: "40%" }}>
                          {s.warn && (
                            <span className="text-danger">⚠ EMI money collected from members</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {!fundingPlan.allowed && (
                      <tr>
                        <td className="text-danger"><strong>Not funded</strong></td>
                        <td className="text-end text-danger">
                          <strong>
                            ₹{Number(fundingPlan.shortfall).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </strong>
                        </td>
                        <td />
                      </tr>
                    )}
                  </tbody>
                </table>
                {fundingPlan.message && (
                  <p className="mb-0 mt-2" style={{ fontSize: "0.85rem" }}>
                    {fundingPlan.message}
                  </p>
                )}
              </Alert>
            )}
            <Form>
              {/* Decides two things: which asset account the payout is posted
                  against, and whether already-overdue instalments are netted off
                  the proceeds. BANK nets them (the money is in the FPO's
                  account); CASH does not, because notes handed over cannot be
                  partly taken back — the member pays those separately. */}
              <Form.Group className="mb-3">
                <Form.Label>Mode of Disbursement</Form.Label>
                <Form.Select
                  value={disbursementMode}
                  onChange={(e) => setDisbursementMode(e.target.value)}
                >
                  <option value="BANK">BANK</option>
                  <option value="CASH">CASH</option>
                </Form.Select>
                <Form.Text className="text-muted">
                  {disbursementMode === "CASH"
                    ? "Overdue instalments stay pending — the member pays them separately."
                    : "Any overdue instalments are deducted from the proceeds."}
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Insurance Amount</Form.Label>
                <Form.Control
                  type="number"
                  value={editableInsuranceAmount}
                  onChange={(e) => setEditableInsuranceAmount(e.target.value)}
                  placeholder="Enter insurance amount"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Security Deposit</Form.Label>
                <Form.Control
                  type="number"
                  value={editableSecurityDeposit}
                  onChange={(e) => setEditableSecurityDeposit(e.target.value)}
                  placeholder="Enter security deposit amount"
                />
              </Form.Group>
              {/* WHERE IS THIS LOAN'S MONEY COMING FROM?
                *
                * Each row is one agency and the amount drawn from it. The agency
                * comes FIRST — the operator picks the source, then says how much
                * — and the amount's label names that agency, so no fixed word can
                * contradict the dropdown.
                *
                * It used to read "Funding Amount Federal (OWN FUND)": the words
                * Federal/Own/Other were hardcoded while the bracket showed the
                * real selection, so the two disagreed whenever an agency sat in a
                * slot its hardcoded word did not match — which is most of the
                * time, since any of the four agencies can go in any slot.
                *
                * fundingRows comes from validationSchema.js so the agency-amount
                * pairing lives in ONE place. It is deliberately out of step —
                * agency 1 pairs with amount 2 — and ledgerPosting.js:2582 reads
                * it that way. Do not "correct" the numbering. */}
              {fundingRows.map((row, index) => {
                const agencyName = agencyNameFor(row.selectedAgency);
                const slotLabel = `Agency ${index + 1}`;
                return (
                  <Fragment key={row.agency}>
                    <Form.Group className="mb-3">
                      <Form.Label>Funding Agency {index + 1}</Form.Label>
                      <Form.Control
                        as="select"
                        value={row.selectedAgency}
                        onChange={(e) => row.setSelectedAgency(e.target.value)}
                      >
                        <option value="">Select Agency</option>
                        {fundingAgencies.map((agency) => (
                          <option key={agency.id} value={agency.id}>
                            {agency.agencyName}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Amount from {agencyName || slotLabel}</Form.Label>
                      <Form.Control
                        type="number"
                        value={row.amountValue}
                        onChange={(e) => row.setAmountValue(e.target.value)}
                        // An amount with no agency is meaningless, and it is the
                        // state that produced the old "(N/A)" label.
                        disabled={!agencyName}
                        placeholder={
                          agencyName ? "Enter amount" : "Choose an agency first"
                        }
                      />
                    </Form.Group>
                  </Fragment>
                );
              })}

              {/* The funding must add up to the loan. addDetails.js and
                * proposedLoanDetailsValidationSchema already REFUSE a loan that
                * does not reconcile — this shows the gap while the operator
                * types, instead of after the submit is rejected. */}
              <div
                className={[
                  "mb-3 small",
                  fundingReconciles ? "text-success" : "text-danger",
                ].join(" ")}
              >
                <strong>Funded:</strong> {inr(fundedTotal)} of {inr(loanAmount)}
                {fundingReconciles
                  ? " ✓"
                  : ` — ${inr(Math.abs(loanAmount - fundedTotal))} ${
                      fundedTotal < loanAmount ? "short" : "over"
                    }`}
              </div>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseDisbursement}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleDisbursementConfirm}
              // The chosen Cash/Bank account cannot cover this loan: nothing to pay
              // it from until a deposit or receipt comes in. The server refuses too.
              disabled={isDisbursing || (fundingPlan && fundingPlan.allowed === false)}>
              {isDisbursing ? "Submitting…" : "Submit Disbursement"}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {showRetriggerModal && (
        <RetriggerModal
          show={showRetriggerModal}
          handleClose={handleCloseRetrigger}
          handleConfirm={handleRetriggerConfirm}
          message={
            <Alert variant="info">
              <p>Are you sure you want to Retrigger?</p>
            </Alert>
          }
          comment={comment}
          setComment={setComment}
          retriggerToRole={retriggerToRole}
          setRetriggerToRole={setRetriggerToRole}
          roleId={roleName}
          item={item}
        />
      )}
      {showRejectModal && (
        <RejectModal
          show={showRejectModal}
          handleClose={handleCloseReject}
          handleConfirm={handleRejectConfirm}
          message={
            <Alert variant="info">
              <p>Are you sure you want to Reject?</p>
            </Alert>
          }
          comment={comment}
          setComment={setComment}
        />
      )}
    </Fragment>
  );
};

export default ActionButtons;












// import React, { useState, useEffect, Fragment } from "react";
// import { useAuth } from "./AuthContext";
// import { Button, Form, Alert, Modal } from "react-bootstrap";
// import RetriggerModal from "./RetriggerModal";
// import RejectModal from "./RejectModal";
// import { API_BASE_URL } from "../config";
// import { TiTick } from "react-icons/ti";
// import { FaArrowRotateLeft } from "react-icons/fa6";
// import { RxCross2 } from "react-icons/rx";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";

// const ActionButtons = ({ updateList, item }) => {
//   const navigate = useNavigate();
//   const { roleName } = useAuth();
//   const [showDisbursementModal, setShowDisbursementModal] = useState(false);
//   const [memberId, setMemberId] = useState("");
//   const [comment, setComment] = useState("");
//   const [retriggerToRole, setRetriggerToRole] = useState("");
//   const [showRetriggerModal, setShowRetriggerModal] = useState(false);
//   const [showRejectModal, setShowRejectModal] = useState(false);
//   const [fundingAgencies, setFundingAgencies] = useState([]);
//   const [selectedFundingAgency1, setSelectedFundingAgency1] = useState("");
//   const [selectedFundingAgency2, setSelectedFundingAgency2] = useState("");
//   const [fundingAmount1, setFundingAmount1] = useState(0);
//   const [fundingAmount2, setFundingAmount2] = useState(0);
//   const [error, setError] = useState("");

//   const loanDetails =
//     item?.fk_member_details_hasOne_proposed_loan_details_memberId || {};
//   const loanAmount = loanDetails.loanAmount || 0;
//   const rateOfInterest = loanDetails.rateOfInterest || 0;
//   const tenureInMonths = loanDetails.tenureInMonths || 0;
//   const initialFundingAmount1 = loanDetails.fundingAmount1 || 0;
//   const initialFundingAmount2 = loanDetails.fundingAmount2 || 0;
//   const initialFundingAgencyId = loanDetails.fundingAgencyId || 0;
//   const initialFundingAgencyId2 = loanDetails.fundingAgencyId2 || 0;

//   const processingCharge = Math.round((loanAmount * 2.5) / 100);
//   const gst = Math.round((processingCharge * 18) / 100);
//   const monthlyRate = rateOfInterest / 12 / 100;
//   const securityDeposit = 0;
//   const insurancePercentage = 2.284;
//   const insuranceAmount = 0;

//   const amountToRelease =
//     loanAmount - processingCharge - gst - securityDeposit - insuranceAmount;

//   useEffect(() => {
//     const fetchFundingAgencies = async () => {
//       try {
//         const response = await axios.get(
//           `${API_BASE_URL}/api/getModelData/funding_agencies`
//         );
//         setFundingAgencies(response.data.data || []);
//         setSelectedFundingAgency1(initialFundingAgencyId);
//         setSelectedFundingAgency2(initialFundingAgencyId2);
//         setFundingAmount1(initialFundingAmount1);
//         setFundingAmount2(initialFundingAmount2);
//       } catch (error) {
//         setError("Failed to fetch funding agencies. Please try again.");
//       }
//     };
//     if (roleName === "admin") {
//       fetchFundingAgencies();
//     }
//   }, [initialFundingAgencyId, initialFundingAgencyId2, roleName, initialFundingAmount1, initialFundingAmount2]);

//   const handleSubmit = async (id, msg, modalName) => {
//     setMemberId(id);
//     setError("");
//     if (roleName === "admin" && modalName === "submit") {
//       setShowDisbursementModal(true);
//     } else {
//       const message = (
//         <Alert variant="info">
//           <p>{msg}</p>
//         </Alert>
//       );
//       if (modalName === "submit") {
//         handleNonAdminSubmit(id, message);
//       } else if (modalName === "retrigger") {
//         setShowRetriggerModal(true);
//       } else if (modalName === "reject") {
//         setShowRejectModal(true);
//       }
//     }
//   };

//   const handleNonAdminSubmit = async (id, message) => {
//     try {
//       setError("");
//       const payload = {
//         model: "member_details",
//         type: "Edit",
//         id: id,
//       };
//       if (roleName === "Data Entry Operator") {
//         payload.dataEntryOperatorStatus = "submitted";
//         payload.dataEntryOperatorStatusUpdatedAt = new Date().toISOString();
//       }
//       await axios.post(`${API_BASE_URL}/api/addDetails`, payload);
//       updateList();
//     } catch (error) {
//       setError("Failed to submit. Please try again.");
//     }
//   };

//   const handleDisbursementConfirm = async () => {
//     try {
//       setError("");
//       // Update proposed_loan_details
//       const fundingPayload = {
//         id: loanDetails.id,
//         model: "proposed_loan_details",
//         type: "Edit",
//         fundingAgencyId: selectedFundingAgency1,
//         fundingAgencyId2: selectedFundingAgency2,
//         fundingAmount1: parseFloat(fundingAmount1) || 0,
//         fundingAmount2: parseFloat(fundingAmount2) || 0,
//       };
//       await axios.post(`${API_BASE_URL}/api/addDetails`, fundingPayload);

//       // Submit disbursement
//       const disbursementPayload = {
//         model: "member_details",
//         type: "Edit",
//         id: memberId,
//         adminStatus: "submitted",
//         adminStatusUpdatedAt: new Date().toISOString(),
//         // processingCharge: processingCharge,
//         isProcessingChargePaid: false,
//         // gst: gst,
//         isGstPaid: false,
//         // securityDeposit: securityDeposit,
//         isSecurityDepositPaid: false,
//         isLoanInsured: true,
//         insuranceAmount: insuranceAmount,
//         isInsuranceAmountPaid: false,
//         // amountToRelease: amountToRelease,
//       };
//       await axios.post(`${API_BASE_URL}/api/addDetails`, disbursementPayload);

//       updateList();
//       setShowDisbursementModal(false);
//     } catch (error) {
//       setError("Failed to submit disbursement. Please try again.");
//     } finally {
//       setComment("");
//     }
//   };

//   const handleCloseDisbursement = () => {
//     setShowDisbursementModal(false);
//     setError("");
//     setSelectedFundingAgency1(initialFundingAgencyId);
//     setSelectedFundingAgency2(initialFundingAgencyId2);
//     setFundingAmount1(initialFundingAmount1);
//     setFundingAmount2(initialFundingAmount2);
//   };

//   const handleRetriggerConfirm = async () => {
//     try {
//       setError("");
//       const formattedMessage =
//         "\n" +
//         comment +
//         "\n---By " +
//         roleName +
//         "\n-- " +
//         new Date().toISOString() +
//         "\n******** ";
//       const payload = {
//         id: memberId,
//         model: "member_details",
//         type: "Edit",
//       };
//       if (retriggerToRole === "Data Entry Operator") {
//         payload.dataEntryOperatorStatus = "retrigger";
//         payload.dataEntryOperatorMessage = formattedMessage;
//         payload.dataEntryOperatorStatusUpdatedAt = new Date().toISOString();
//       }
//       if (retriggerToRole === "Branch Manager") {
//         payload.branchManagerStatus = "retrigger";
//         payload.branchManagerMessage = formattedMessage;
//         payload.branchManagerStatusUpdatedAt = new Date().toISOString();
//       }
//       await axios.post(`${API_BASE_URL}/api/addDetails`, payload);
//       updateList();
//     } catch (error) {
//       setError("Failed to retrigger. Please try again.");
//     } finally {
//       setShowRetriggerModal(false);
//       setComment("");
//       setRetriggerToRole("");
//     }
//   };

//   const handleCloseRetrigger = () => {
//     setShowRetriggerModal(false);
//     setComment("");
//     setRetriggerToRole("");
//     setError("");
//   };

//   const handleRejectConfirm = async () => {
//     try {
//       setError("");
//       const formattedMessage =
//         "\n" +
//         comment +
//         "\n---By " +
//         roleName +
//         "\n-- " +
//         new Date().toISOString() +
//         "\n******** ";
//       const payload = {
//         model: "member_details",
//         type: "Edit",
//         id: memberId,
//       };
//       if (roleName === "Data Entry Operator") {
//         payload.dataEntryOperatorStatus = "rejected";
//         payload.dataEntryOperatorMessage = formattedMessage;
//         payload.dataEntryOperatorStatusUpdatedAt = new Date().toISOString();
//       }
//       if (roleName === "Branch Manager") {
//         payload.branchManagerStatus = "rejected";
//         payload.branchManagerMessage = formattedMessage;
//         payload.branchManagerStatusUpdatedAt = new Date().toISOString();
//       }
//       if (roleName === "admin") {
//         payload.adminStatus = "rejected";
//         payload.adminMessage = formattedMessage;
//         payload.adminStatusUpdatedAt = new Date().toISOString();
//       }
//       await axios.post(`${API_BASE_URL}/api/addDetails`, payload);
//       updateList();
//     } catch (error) {
//       setError("Failed to reject. Please try again.");
//     } finally {
//       setShowRejectModal(false);
//       setComment("");
//     }
//   };

//   const handleCloseReject = () => {
//     setShowRejectModal(false);
//     setComment("");
//     setError("");
//   };

//   return (
//     <Fragment>
//       {error && (
//         <Alert variant="danger" onClose={() => setError("")} dismissible>
//           {error}
//         </Alert>
//       )}
//       <div className="d-flex">
//         {((roleName === "Data Entry Operator" &&
//           ["pending", "retrigger"].includes(item.dataEntryOperatorStatus)) ||
//           (roleName === "admin" &&
//             item.dataEntryOperatorStatus === "submitted" &&
//             item.branchManagerStatus === "submitted" &&
//             ["pending", "retrigger"].includes(item.adminStatus))) && (
//           <Button
//             variant="success"
//             onClick={() =>
//               handleSubmit(item.id, "Are you sure you want to Submit?", "submit")
//             }
//             className="mx-1 btn-hover"
//             size={roleName === "admin" ? "sm" : "md"}
//           >
//             <TiTick size={roleName === "admin" ? 16 : 20} />
//           </Button>
//         )}
//         {((roleName === "Branch Manager" &&
//           item.dataEntryOperatorStatus === "submitted" &&
//           item.branchManagerStatus === "pending") ||
//           (roleName === "admin" &&
//             item.dataEntryOperatorStatus === "submitted" &&
//             item.branchManagerStatus === "submitted" &&
//             item.adminStatus === "pending")) && (
//           <Button
//             variant="warning"
//             onClick={() =>
//               handleSubmit(
//                 item.id,
//                 "Are you sure you want to Retrigger?",
//                 "retrigger"
//               )
//             }
//             className="mx-1 btn-hover"
//             size={roleName === "admin" ? "sm" : "md"}
//           >
//             <FaArrowRotateLeft size={roleName === "admin" ? 14 : 17} />
//           </Button>
//         )}
//         {((roleName === "Data Entry Operator" &&
//           ["pending", "retrigger"].includes(item.dataEntryOperatorStatus)) ||
//           (roleName === "Branch Manager" &&
//             item.dataEntryOperatorStatus === "submitted" &&
//             ["pending", "retrigger"].includes(item.branchManagerStatus)) ||
//           (roleName === "admin" &&
//             item.dataEntryOperatorStatus === "submitted" &&
//             item.branchManagerStatus === "submitted" &&
//             ["pending", "retrigger"].includes(item.adminStatus))) && (
//           <Button
//             variant="danger"
//             onClick={() =>
//               handleSubmit(item.id, "Are you sure you want to Reject?", "reject")
//             }
//             className="mx-1 btn-hover"
//             size={roleName === "admin" ? "sm" : "md"}
//           >
//             <RxCross2 size={roleName === "admin" ? 16 : 20} />
//           </Button>
//         )}
//       </div>
//       {showDisbursementModal && (
//         <Modal show={showDisbursementModal} onHide={handleCloseDisbursement}>
//           <Modal.Header closeButton>
//             <Modal.Title>Loan Disbursement</Modal.Title>
//           </Modal.Header>
//           <Modal.Body>
//             {error && <Alert variant="danger">{error}</Alert>}
//             <Alert variant="info">
//               <p>
//                 {/* <strong>Processing Charge:</strong> {processingCharge} <br /> */}
//                 {/* <strong>GST:</strong> {gst} <br /> */}
//                 {/* <strong>Amount to Release:</strong> {amountToRelease} <br /> */}
//               </p>
//             </Alert>
//             <Form>
//               <Form.Group className="mb-3">
//                 <Form.Label>Funding Amount Federal ({
//                   fundingAgencies.find((agency) => agency.id === selectedFundingAgency1)?.agencyName || "N/A"
//                 })</Form.Label>
//                 <Form.Control
//                   type="number"
//                   value={fundingAmount1}
//                   onChange={(e) => setFundingAmount1(e.target.value)}
//                   placeholder="Enter amount"
//                 />
//               </Form.Group>
//               <Form.Group className="mb-3">
//                 <Form.Label>Funding Agency 1</Form.Label>
//                 <Form.Control
//                   as="select"
//                   value={selectedFundingAgency1}
//                   onChange={(e) => setSelectedFundingAgency1(e.target.value)}
//                 >
//                   <option value="">Select Agency</option>
//                   {fundingAgencies.map((agency) => (
//                     <option key={agency.id} value={agency.id}>
//                       {agency.agencyName}
//                     </option>
//                   ))}
//                 </Form.Control>
//               </Form.Group>
//               <Form.Group className="mb-3">
//                 <Form.Label>Funding Amount Own ({
//                   fundingAgencies.find((agency) => agency.id === selectedFundingAgency2)?.agencyName || "N/A"
//                 })</Form.Label>
//                 <Form.Control
//                   type="number"
//                   value={fundingAmount2}
//                   onChange={(e) => setFundingAmount2(e.target.value)}
//                   placeholder="Enter amount"
//                 />
//               </Form.Group>
//               <Form.Group className="mb-3">
//                 <Form.Label>Funding Agency 2</Form.Label>
//                 <Form.Control
//                   as="select"
//                   value={selectedFundingAgency2}
//                   onChange={(e) => setSelectedFundingAgency2(e.target.value)}
//                 >
//                   <option value="">Select Agency</option>
//                   {fundingAgencies.map((agency) => (
//                     <option key={agency.id} value={agency.id}>
//                       {agency.agencyName}
//                     </option>
//                   ))}
//                 </Form.Control>
//               </Form.Group>
//             </Form>
//           </Modal.Body>
//           <Modal.Footer>
//             <Button variant="secondary" onClick={handleCloseDisbursement}>
//               Cancel
//             </Button>
//             <Button variant="primary" onClick={handleDisbursementConfirm}>
//               Submit Disbursement
//             </Button>
//           </Modal.Footer>
//         </Modal>
//       )}
//       {showRetriggerModal && (
//         <RetriggerModal
//           show={showRetriggerModal}
//           handleClose={handleCloseRetrigger}
//           handleConfirm={handleRetriggerConfirm}
//           message={
//             <Alert variant="info">
//               <p>Are you sure you want to Retrigger?</p>
//             </Alert>
//           }
//           comment={comment}
//           setComment={setComment}
//           retriggerToRole={retriggerToRole}
//           setRetriggerToRole={setRetriggerToRole}
//           roleId={roleName}
//           item={item}
//         />
//       )}
//       {showRejectModal && (
//         <RejectModal
//           show={showRejectModal}
//           handleClose={handleCloseReject}
//           handleConfirm={handleRejectConfirm}
//           message={
//             <Alert variant="info">
//               <p>Are you sure you want to Reject?</p>
//             </Alert>
//           }
//           comment={comment}
//           setComment={setComment}
//         />
//       )}
//     </Fragment>
//   );
// };

// export default ActionButtons;

// import React, { useState, useEffect, useCallback, Fragment } from "react";
// import { useAuth } from "./AuthContext";
// import { Button } from "react-bootstrap";
// import ApproveModal from "./ApproveModal";
// import RetriggerModal from "./RetriggerModal";
// import RejectModal from "./RejectModal";
// import { API_BASE_URL } from "../config";
// import { TiTick } from "react-icons/ti";
// import { FaArrowRotateLeft } from "react-icons/fa6";
// import { RxCross2 } from "react-icons/rx";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";
// import ApproveCmtModal from "./ApproveCmtModal";

// const ActionButtons = ({ updateList, item }) => {
//   const navigate = useNavigate();
//   const { roleName } = useAuth();
//   const [showApproveModal, setShowApproveModal] = useState(false);
//   const [showApproveCmtModal, setShowApproveCmtModal] = useState(false);
//   const [memberId, setMemberId] = useState("");
//   const [message, setMessage] = useState("");
//   const [comment, setComment] = useState("");
//   const [retriggerToRole, setRetriggerToRole] = useState("");
//   const [showRetriggerModal, setShowRetriggerModal] = useState(false);
//   const [showRejectModal, setShowRejectModal] = useState(false);

//   // const loanDetails =
//   //   item?.fk_member_details_hasOne_proposed_loan_details_memberId;
//   // const loanAmount = loanDetails?.loanAmount || 0;
//   // const rateOfInterest =
//   //   item.fk_member_details_hasOne_proposed_loan_details_memberId
//   //     .rateOfInterest || 0;
//   // const tenureInMonths =
//   //   item.fk_member_details_hasOne_proposed_loan_details_memberId
//   //     .tenureInMonths || 0;

//   const loanDetails =
//     item?.fk_member_details_hasOne_proposed_loan_details_memberId || {};
//   const loanAmount = loanDetails?.loanAmount || 0;
//   const rateOfInterest = loanDetails?.rateOfInterest || 0;
//   const tenureInMonths = loanDetails?.tenureInMonths || 0;
//   const fundingAmount1 = loanDetails?.fundingAmount1 || 0;
//   const fundingAmount2 = loanDetails?.fundingAmount2 || 0;
//   const fundingAgencyId = loanDetails?.fundingAgencyId || 0;
//   const fundingAgencyId2 = loanDetails?.fundingAgencyId2|| 0;


//   const processingCharge = Math.round((loanAmount * 2.5) / 100);
//   const gst = Math.round((processingCharge * 18) / 100);
//   const monthlyRate = rateOfInterest / 12 / 100;
//   const securityDeposit = 0;
//   const insurancePercentage = 2.284;
//   const insuranceAmount = 0;

//   const amountToRelease =
//     loanAmount - processingCharge - gst - securityDeposit - insuranceAmount;

//   const handleSubmit = async (id, msg, modalName) => {
//     setMemberId(id);
//     if (roleName === "admin" && modalName === "submit") {
//       const message = (
//         <>
//           Processing Charge: {processingCharge} <br />
//           GST: {gst} <br />
//           Funding amount Fedaral: {fundingAmount1} <br />
//           Funding amount Own: {fundingAmount2} <br />
//           Amount to Release: {amountToRelease} <br />
//           Fundind Agency 1: {fundingAgencyId} <br />
//           Fundind Agency 2: {fundingAgencyId2} <br />
        
//           <h3>Are you sure you want to Disburse Loan?</h3>
//         </>
//       );
//       setMessage(message);
//     } else {
//       setMessage(msg);
//     }

//     if (modalName === "submit") {
//       setShowApproveModal(true);
//     } else if (modalName === "retrigger") {
//       setShowRetriggerModal(true);
//     } else if (modalName === "reject") {
//       setShowRejectModal(true);
//     }
//   };

//   const currentISTDateTime = new Date().toISOString();

//   const handleApproveConfirm = async () => {
//     try {
//       const payload = {};
//       if (roleName === "Data Entry Operator") {
//         payload.id = memberId;
//         payload.dataEntryOperatorStatus = "submitted";
//         payload.dataEntryOperatorStatusUpdatedAt = currentISTDateTime;
//       }
//       if (roleName === "admin") {
//         payload.id = memberId;
//         payload.adminStatus = "submitted";
//         payload.adminStatusUpdatedAt = currentISTDateTime;
//         payload.processingCharge = processingCharge;
//         payload.isProcessingChargePaid = false;
//         payload.gst = gst;
//         payload.isGstPaid = false;
//         payload.securityDeposit = securityDeposit;
//         payload.isSecurityDepositPaid = false;
//         payload.isLoanInsured = true;
//         payload.insuranceAmount = insuranceAmount;
//         payload.isInsuranceAmountPaid = false;
//         payload.amountToRelease = amountToRelease;
//       }
//       payload.model = "member_details";
//       payload.type = "Edit";
//       await axios.post(`${API_BASE_URL}/api/addDetails`, {
//         ...payload,
//       });
//       updateList();
//     } catch (error) {
//       console.log("Error fetching list:", error);
//     } finally {
//       setShowApproveModal(false);
//     }
//   };

//   const handleCloseApprove = async () => {
//     setShowApproveModal(false);
//   };

//   const handleRetriggerConfirm = async () => {
//     try {
//       const payload = {};
//       const formattedMessage =
//         "\n" +
//         comment +
//         "\n---By " +
//         roleName +
//         "\n-- " +
//         currentISTDateTime +
//         "\n******** ";
//       payload.id = memberId;
//       payload.model = "member_details";
//       payload.type = "Edit";
//       if (retriggerToRole === "Data Entry Operator") {
//         payload.dataEntryOperatorStatus = "retrigger";
//         payload.dataEntryOperatorMessage = formattedMessage;
//         payload.dataEntryOperatorStatusUpdatedAt = currentISTDateTime;
//       }
//       if (retriggerToRole === "Branch Manager") {
//         payload.branchManagerStatus = "retrigger";
//         payload.branchManagerMessage = formattedMessage;
//         payload.branchManagerStatusUpdatedAt = currentISTDateTime;
//       }

//       await axios.post(`${API_BASE_URL}/api/addDetails`, {
//         ...payload,
//       });
//       updateList();
//     } catch (error) {
//       console.log("Error fetching list:", error);
//     } finally {
//       setShowRetriggerModal(false);
//     }
//   };

//   const handleCloseRetrigger = async () => {
//     setShowRetriggerModal(false);
//   };

//   const handleRejectConfirm = async () => {
//     try {
//       const payload = {};
//       const formattedMessage =
//         "\n" +
//         comment +
//         "\n---By " +
//         roleName +
//         "\n-- " +
//         currentISTDateTime +
//         "\n******** ";
//       payload.model = "member_details";
//       payload.type = "Edit";
//       if (roleName === "Data Entry Operator") {
//         payload.id = memberId;
//         payload.dataEntryOperatorStatus = "rejected";
//         payload.dataEntryOperatorMessage = formattedMessage;
//         payload.dataEntryOperatorStatusUpdatedAt = currentISTDateTime;
//       }
//       if (roleName === "Branch Manager") {
//         payload.id = memberId;
//         payload.branchManagerStatus = "rejected";
//         payload.branchManagerMessage = formattedMessage;
//         payload.branchManagerStatusUpdatedAt = currentISTDateTime;
//       }
//       if (roleName === "admin") {
//         payload.id = memberId;
//         payload.adminStatus = "rejected";
//         payload.adminMessage = formattedMessage;
//         payload.adminStatusUpdatedAt = currentISTDateTime;
//       }

//       await axios.post(`${API_BASE_URL}/api/addDetails`, {
//         ...payload,
//       });
//       updateList();
//     } catch (error) {
//       console.log("Error fetching list:", error);
//     } finally {
//       setShowRejectModal(false);
//     }
//   };

//   const handleCloseReject = async () => {
//     setShowRejectModal(false);
//   };

//   return (
//     <Fragment>
//       {((roleName === "Data Entry Operator" &&
//         ["pending", "retrigger"].includes(item.dataEntryOperatorStatus)) ||
//         (roleName === "admin" &&
//           item.dataEntryOperatorStatus === "submitted" &&
//           item.branchManagerStatus === "submitted" &&
//           ["pending", "retrigger"].includes(item.adminStatus))) && (
//         <Button 
        
//           variant="success"
//           onClick={() =>
//             handleSubmit(item.id, "Are you sure you want to Submit?", "submit")
//           }
//           className="mx-1 btn-hover"
//         >
//           <TiTick size={20} />
//         </Button>
//       )}
//       {((roleName === "Branch Manager" &&
//         item.dataEntryOperatorStatus === "submitted" &&
//         item.branchManagerStatus === "pending") ||
//         (roleName === "admin" &&
//           item.dataEntryOperatorStatus === "submitted" &&
//           item.branchManagerStatus === "submitted" &&
//           item.adminStatus === "pending")) && (
//         <Button
//           variant="warning"
//           onClick={() =>
//             handleSubmit(
//               item.id,
//               "Are you sure you want to Retrigger?",
//               "retrigger"
//             )
//           }
//           className="mx-1 btn-hover"
//         >
//           <FaArrowRotateLeft size={17} />
//         </Button>
//       )}
//       {((roleName === "Data Entry Operator" &&
//         ["pending", "retrigger"].includes(item.dataEntryOperatorStatus)) ||
//         (roleName === "Branch Manager" &&
//           item.dataEntryOperatorStatus === "submitted" &&
//           ["pending", "retrigger"].includes(item.branchManagerStatus)) ||
//         (roleName === "admin" &&
//           item.dataEntryOperatorStatus === "submitted" &&
//           item.branchManagerStatus === "submitted" &&
//           ["pending", "retrigger"].includes(item.adminStatus))) && (
//         <Button
//           variant="danger"
//           onClick={() =>
//             handleSubmit(item.id, "Are you sure you want to Reject?", "reject")
//           }
//           className="mx-1 btn-hover"
//         >
//           <RxCross2 size={20} />
//         </Button>
//       )}
//       {showApproveModal && (
//         <ApproveModal
//           show={showApproveModal}
//           handleClose={handleCloseApprove}
//           handleConfirm={handleApproveConfirm}
//           message={message}
//         />
//       )}
//       {showRetriggerModal && (
//         <RetriggerModal
//           show={showRetriggerModal}
//           handleClose={handleCloseRetrigger}
//           handleConfirm={handleRetriggerConfirm}
//           message={message}
//           comment={comment}
//           setComment={setComment}
//           retriggerToRole={retriggerToRole}
//           setRetriggerToRole={setRetriggerToRole}
//           roleId={roleName}
//           item={item}
//         />
//       )}
//       {showRejectModal && (
//         <RejectModal
//           show={showRejectModal}
//           handleClose={handleCloseReject}
//           handleConfirm={handleRejectConfirm}
//           message={message}
//           comment={comment}
//           setComment={setComment}
//         />
//       )}
//     </Fragment>
//   );
// };

// export default ActionButtons;